/**
 * What the person is looking at.
 *
 * A screen that has something to say publishes a context: its title, a few
 * lines that summarise it, the material and figure set its own engine
 * produced, the questions it can answer itself, and two or three things it
 * would offer. Sky reads the current context when she greets, when she is
 * asked "what am I looking at", and before she reaches for anything wider —
 * a question asked on the Cash desk is a question about cash until proven
 * otherwise.
 *
 * Screens that publish nothing still get a description: the guide knows what
 * every screen is for, and Sky says that rather than pretending to a reading
 * she does not have.
 */
import { useEffect } from "react";
import { screenLevel, type AccessLevel } from "./access";
import { MODULES } from "./guide";

export interface ScreenOffer {
  label: string;
  /** what Sky asks herself when the offer is pressed */
  ask: string;
}

export interface ScreenContext {
  route: string;
  title: string;
  /** the three lines the screen would give a board, or the reading it draws */
  summary: string[];
  /** everything the screen's engine computed, for a connected model */
  material?: string;
  /** every figure the engine emitted; a model reply carrying any other is withheld */
  figures?: Set<string>;
  /** the screen's own answerer, before any search */
  own?: (q: string) => string | null;
  offers: ScreenOffer[];
  /** the system prompt for a connected model on this screen */
  prompt?: string;
  /**
   * A desk people WORK at rather than read.
   *
   * Most desks are dense readings — a decision with its working, a cash
   * position with its assumptions — and leading with a summary wins the first
   * ten minutes. A stores counter is not that. Its whole content is things to
   * do: book a delivery in, load the stock, hand parts over. Hiding those
   * behind "open the full desk" means somebody opens the screen, reads four
   * sentences, and concludes the features are not there.
   *
   * Such a desk still publishes its summary — Sky and the boards read it — it
   * simply does not open on it.
   */
  worksInFull?: boolean;
  level: AccessLevel;
}

let current: ScreenContext | null = null;
const listeners = new Set<() => void>();

export function getScreen(): ScreenContext | null {
  return current;
}

export function subscribeScreen(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function publish(next: ScreenContext | null) {
  current = next;
  for (const l of listeners) l();
}

/** A screen calls this with what it knows; null while it is still loading. */
export function useScreenContext(ctx: Omit<ScreenContext, "level"> | null): void {
  const key = ctx ? `${ctx.route}|${ctx.title}|${ctx.summary.join("|")}|${ctx.offers.map((o) => o.ask).join("|")}` : "";
  useEffect(() => {
    if (!ctx) return;
    publish({ ...ctx, level: screenLevel(ctx.route) });
    return () => {
      if (current?.route === ctx.route) publish(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ctx?.own, ctx?.material]);
}

/** "What am I looking at?" and its relatives. */
export function isAboutScreen(q: string): boolean {
  return /\b(this (screen|page|desk|room|view)|what am i (looking at|on)|where am i|what is this|what's this|whats this|explain (this|the screen|the page)|summar(y|ise|ize) (this|the screen|the page|it)|walk me through|help me here|what can you (do|tell me) here|what('s| is) here|what does this (show|mean)|read (me )?this)\b/i.test(
    q,
  );
}

/** A description for any route, from the guide, when the screen published nothing. */
export function describeRoute(route: string): { title: string; summary: string[]; offers: ScreenOffer[] } | null {
  const path = route.split("?")[0];
  const m = MODULES.find((x) => x.route === path) ?? MODULES.find((x) => path.startsWith(`${x.route}/`) && x.route !== "/");
  if (!m) return null;
  return {
    title: m.name,
    summary: [m.what, m.how, ...(m.look ? [`Worth knowing: ${m.look}`] : [])],
    offers: [
      { label: "How does it work?", ask: `how does ${m.name} work` },
      { label: "What should I look at first?", ask: "where do I start" },
    ],
  };
}

/** What Sky says on arriving at a screen, once. */
export function arrivalLine(ctx: Pick<ScreenContext, "title" | "summary">): string {
  const first = ctx.summary[0] ?? "";
  return `You are on ${ctx.title}. ${first}`.trim();
}
